import {defineComponent, ref, computed, watch, onMounted, inject,} from "vue";
import {IInvoiceListItem} from "@/api/klip-api.proxy";
import {Mri} from "@/app/mri/shared/services/services";
import DateUtil from '@/app/shared/helpers/date-util';
import {useIsMobile} from '@/app/shared/composables/useIsMobile';
import {storeToRefs} from 'pinia';
import {useKlInvoicesStore} from '@/app/shared/components/kl-invoices/kl-invoices-store';
import KlInvoiceCard from '@/app/shared/components/kl-invoices/kl-invoice-card/kl-invoice-card.vue';

export default defineComponent({
    name: 'KlInvoices',
    components: {KlInvoiceCard},
    props: {
        organisationId: {
            type: String,
            default: null,
            required: false
        },
        userId: {
            type: String,
            default: null,
            required: false
        },
        showOrganisationName: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {

        const invoicesStore = useKlInvoicesStore();
        const { fetching, invoices, unAvailable, downloadFileUri, searchFields } = storeToRefs(invoicesStore);

        const filterToggleInCardView = ref<boolean>(false);

        const useCards = computed(() => {
            const isPhoneSize = useIsMobile();
            return isPhoneSize.value;
        });

        const showFilter = computed(() => {
            return !useCards.value || filterToggleInCardView.value;
        });

        const meta = ref<{
            totalRows: number;
            resultsPerPage: number;
        }>({totalRows: 0, resultsPerPage: 50});

        const downloadFileUriLoading = ref<boolean>(false);

        const $refs = ref<{
            searchInvoices: any;
        }>(undefined);

        const showDownloadLink = computed((): boolean => {
            return !!invoices.value && invoices.value.length > 0;
        });

        const showFinancialServiceWarning = computed((): boolean => {
            return !!props.organisationId && !!props.userId;
        });

        const search = async (newSearch: Mri.IInvoicesSearchFields) => {
            await invoicesStore.searchInvoices(props.userId, props.organisationId, newSearch);
        };

        const onInvoicesChanged = (invoices: IInvoiceListItem[]) => {
            meta.value.totalRows = invoices != null ? invoices.length : 0;
        };

        const clearSearch = () => {
            $refs.value.searchInvoices.reset();
        };

        const cellParser = (row: any, column: IColumn) => {
            switch (column.key) {
                case "submitTimestamp":
                    return {
                        template: `<div>${DateUtil.formatDate(row[column.key])}</div>`,
                    };
                // case "totalPrice":
                //     return {
                //         template: `<div class="vl-u-align-right">€${row[column.key]}</div>`,
                //     };
                case "totalPriceFormatted": {
                    if (row.isTopUpCredit) {
                        return { template: `<span class="vl-u-align-right" style="color: #2cac36;">+ ${row[column.key]}</span>` }
                    }
                    else {
                        return { template: `<span class="vl-u-align-right">${row[column.key]}</span>` }
                    }
                }
                case "invoicePdfUrl":
                    return {
                        template: `${
                            row[column.key]
                                ? `<vl-link href="${
                                    row[column.key]
                                }" mod-icon-only icon="file-office-pdf" hidden-text="Download als PDF" class="vl-u-align-center"></vl-link>`
                                : "<span></span>"
                        }`,
                    };
                case "attachmentCsvUrl":
                    return {
                        template: `${
                            row[column.key]
                                ? `<vl-link href="${
                                    row[column.key]
                                }" mod-icon-only icon="file-office-xls" hidden-text="Download als CSV" class="vl-u-align-center"></vl-link>`
                                : "<span></span>"
                        }`,
                    };
                default:
                    if (!row[column.key]) {
                        return {template: `<span></span>`};
                    }
                    return {template: `<div>${row[column.key]}</div>`};
            }
        };

        const columns = ref<IColumn[]>([
            {
                key: 'submitTimestamp',
                label: 'Datum',
                sortable: false,
                isVisible: true,
                width: 7,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'documentReference',
                label: 'Doc nr',
                sortable: false,
                isVisible: true,
                width: 12,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'organisationName',
                label: 'Ontvanger',
                sortable: false,
                isVisible: props.showOrganisationName,
                width: 0,
                widthUnit: '%',
                parser: cellParser,
            },
            {
                key: 'customerReference',
                label: 'Eigen referentie',
                sortable: false,
                isVisible: true,
                width: 0,
                widthUnit: '%',
                parser: cellParser,
            },
            {
                key: 'paymentType',
                label: 'Inhoud',
                sortable: false,
                isVisible: true,
                width: 0,
                widthUnit: '%',
                parser: cellParser,
            },
            {
                key: 'totalPriceFormatted',
                label: 'Bedrag',
                sortable: false,
                isVisible: true,
                width: 7,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'invoicePdfUrl',
                label: 'PDF',
                sortable: false,
                isVisible: true,
                width: 3,
                widthUnit: 'em',
                parser: cellParser,
            },
            {
                key: 'attachmentCsvUrl',
                label: 'CSV',
                sortable: false,
                isVisible: true,
                width: 3,
                widthUnit: 'em',
                parser: cellParser,
            }
        ]);

        watch(invoices, onInvoicesChanged, {
            immediate: false,
            deep: true,
        });

        onMounted(async () => {
            await invoicesStore.fetchInvoices(props.userId, props.organisationId);
        });

        const showUnavailable = computed((): boolean => {
            return unAvailable.value;
        });

        return {
            useCards,
            filterToggleInCardView,
            showFilter,

            fetching,
            invoices,
            meta,
            columns,
            downloadFileUriLoading,
            downloadFileUri,
            searchFields,
            showDownloadLink,
            showUnavailable,
            showFinancialServiceWarning,

            search,
            clearSearch,
        };
    },
});
